import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  Stock,
  StockAddPayload,
  StockDateUpdatePayload,
  StockListParams,
  StockListResponse,
  StockRegistryCompanyOption,
} from '../types'

export async function getStocks(
  params: StockListParams = {},
): Promise<StockListResponse> {
  try {
    const response = await apiClient.get<StockListResponse>('/api/stock', { params })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load stock registry')
  }
}

export async function getStockLogs(stockId: number): Promise<Stock> {
  try {
    const response = await apiClient.get<Stock>(`/api/stock/${stockId}`)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load stock logs')
  }
}

export async function addStock(payload: StockAddPayload): Promise<Stock> {
  try {
    const formData = new FormData()
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (value instanceof File) {
        formData.append(key, value, value.name)
      } else {
        formData.append(key, String(value))
      }
    })
    const response = await apiClient.post<Stock>('/api/stock', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to add stock certificate')
  }
}

export async function updateStockDates(
  stockId: number,
  payload: StockDateUpdatePayload,
): Promise<Stock> {
  try {
    const response = await apiClient.post<Stock>(`/api/stock/${stockId}`, payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update stock dates')
  }
}

export async function activateStock(stockId: number): Promise<void> {
  try {
    await apiClient.post('/api/active_stock', { stock_id: stockId })
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to activate certificate')
  }
}

export async function revokeStocks(stockIds: number[]): Promise<void> {
  try {
    await apiClient.post('/api/revoke_stock', { stock_id: stockIds })
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to revoke certificate(s)')
  }
}

export async function searchCompaniesForStock(query?: string): Promise<StockRegistryCompanyOption[]> {
  try {
    const params: Record<string, string> = { page: 'stock' }
    if (query) {
      params.q = query
    }
    const response = await apiClient.get<Array<{ company_id: number; company_name: string }>>(
      '/api/all_company',
      { params },
    )
    return (response.data ?? []).map((c) => ({ label: c.company_name, value: c.company_id }))
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load companies')
  }
}

export type { StockRegistryCompanyOption } from '../types/services'

